import {useLayoutEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";

import {clearPhoneVerificationTimerLS} from "@pg-mono/user";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {useMultileadTinderAbTest} from "../../ab_testing/hooks/use_multilead_tinder_ab_test";
import {openTinderMultiLeadModal} from "../../application/state/tinder_multi_lead_slice";
import {IApplicationData} from "../../application/types/IApplicationData";
import {ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {useAppSelector} from "../../store/hooks";
import {getUserSourceDisplay} from "../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../user/api/patch_home_match_configuration";
import {HomeMatchBasicPreferencesFlow} from "../../user/components/home_match_basic_preferences_flow/HomeMatchBasicPreferencesFlow";
import {UserPreferencesUpdateDataModal} from "../../user/components/preferences/UserPreferencesUpdateDataModal";
import {UserPhoneVerification} from "../../user/components/UserPhoneVerification";
import {UserUpdatePhoneNumberModal} from "../../user/components/UserUpdatePhoneNumberModal";
import {
    HomeMatchBasicPreferencesFlowCompletionReason,
    HomeMatchBasicPreferencesFlowInitialStep,
    HomeMatchBasicPreferencesFlowSource
} from "../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {closeFlow, nextStep, RegistrationFlowStep, setStep} from "../state/registration_flow_slice";
import {AuthModalVariant} from "./auth_modal/AuthModalHeaderVariants";
import {LeadAfterRegistration} from "./registration_flow/LeadAfterRegistration";
import {RegistrationMultilead} from "./registration_flow/RegistrationMultilead";
import {AuthModalRegistrationFlow} from "./AuthModalRegistrationFlow";

export const RegistrationFlow = () => {
    const dispatch = useDispatch();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const {hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {saveNewsletterSearchData, subscribeToNotifications} = useSubscribeToNewsletter();
    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const {isMultileadTinderEnabled} = useMultileadTinderAbTest();
    const postPriceRevealOverlayVariant = flowState.useLeadAfterRegistration ? "strong" : undefined;
    const homeMatchBasicEventCategory = flowState.gtmSourceSection || (flowState.userSource ? getUserSourceDisplay(flowState.userSource) : "");
    const [homeMatchInitials, setHomeMatchInitials] = useState<{
        source: HomeMatchBasicPreferencesFlowSource;
        initialStep: HomeMatchBasicPreferencesFlowInitialStep;
        consent: boolean;
    }>({
        source: "survey",
        initialStep: "survey",
        consent: false
    });
    const homeMatchBasicModalEventName = homeMatchInitials.source === "survey" ? "modal_post_sign_up" : "modal_post_lead";
    // Set when the price-reveal lead is sent; the multilead runs right after it and `wasOpened` marks
    // that this multilead belongs to the sent lead, so the preferences survey follows it.
    const [postLeadMultilead, setPostLeadMultilead] = useState<{
        refererApplicationUuid: string | null;
        wasOpened: boolean;
        applicationResponse: IApplicationData | null;
    } | null>(null);
    const isPostLeadMultilead = Boolean(postLeadMultilead?.wasOpened);
    const tinderMultiLead = useAppSelector((state) => state.tinderMultiLead);
    const lastTinderMultiLeadRef = useRef(tinderMultiLead);
    const wasFlowActiveRef = useRef(flowState.isActive);
    // In the Tinder AB test variant the deck replaces the classic multilead modal, so the flow parks on
    // MULTILEAD (rendering nothing) until the deck closes and the preferences survey can take over.
    const isTinderDeckParked = isMultileadTinderEnabled && isPostLeadMultilead && flowState.currentStep === RegistrationFlowStep.MULTILEAD;

    const continueToPreferences = (isHomeMatchOn: boolean, surveySource: HomeMatchBasicPreferencesFlowSource) => {
        setHomeMatchInitials({source: surveySource, initialStep: isHomeMatchOn ? "survey" : "summary", consent: isHomeMatchOn});
        dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
    };

    useLayoutEffect(() => {
        const previousTinderMultiLead = lastTinderMultiLeadRef.current;
        lastTinderMultiLeadRef.current = tinderMultiLead;

        if (!previousTinderMultiLead?.isOpen || tinderMultiLead?.isOpen || !isTinderDeckParked) {
            return;
        }

        continueToPreferences(homeMatchConfig?.is_active ?? false, "afterLead");
    }, [tinderMultiLead, isTinderDeckParked]);

    // This component never unmounts (it early-returns while inactive), so without this reset the
    // previous run's sent lead would leak into the next flow's multilead and mark its offer as sent
    useLayoutEffect(() => {
        const wasActive = wasFlowActiveRef.current;
        wasFlowActiveRef.current = flowState.isActive;

        if (!wasActive && flowState.isActive) {
            setPostLeadMultilead(null);
        }
    }, [flowState.isActive]);

    if (!flowState.isActive) {
        return null;
    }

    // The preferences survey keeps the `afterLead` source for a lead that was already sent, so the
    // HomeMatch source section and the modal GTM events stay comparable with the previous order.
    const getSurveySourceAfterMultilead = (multileadSource: HomeMatchBasicPreferencesFlowSource) => (isPostLeadMultilead ? "afterLead" : multileadSource);

    // Multilead (classic or Tinder, same AB test) shown right after the price-reveal lead was sent
    const openPostLeadMultilead = (sentApplication: IApplicationData | null) => {
        const {propertyId, offerId, vendorId} = flowState;
        const refererApplicationUuid = sentApplication?.uuid ?? null;

        if (!propertyId || !offerId || !vendorId) {
            // No offers to recommend - keep the uuid for the survey analytics and fall through to the survey
            setPostLeadMultilead({refererApplicationUuid, wasOpened: false, applicationResponse: sentApplication});
            return false;
        }

        setPostLeadMultilead({refererApplicationUuid, wasOpened: true, applicationResponse: sentApplication});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));

        if (isMultileadTinderEnabled) {
            dispatch(
                openTinderMultiLeadModal({
                    originPropertyId: propertyId,
                    originOfferId: offerId,
                    originVendorId: vendorId,
                    refererApplicationUuid,
                    sourceSection: ApplicationSourceSection.POPUP_MODAL,
                    customSourceSection: "Multilead Tinder - post price reveal lead",
                    overlayVariant: postPriceRevealOverlayVariant
                })
            );
        }

        return true;
    };

    const handlers = {
        onNextStep: () => {
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onLeadConfirm: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentApplication: IApplicationData | null = null) => {
            // Reversed order: propose the extra offers first, ask about preferences afterwards
            if (source === "afterLead" && openPostLeadMultilead(sentApplication)) {
                return;
            }

            continueToPreferences(isHomeMatchOn, source);
        },
        onMultileadComplete: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource) => {
            continueToPreferences(isHomeMatchOn, getSurveySourceAfterMultilead(source));
        },
        onMultileadClose: (isHomeMatchOn: boolean) => {
            continueToPreferences(isHomeMatchOn, getSurveySourceAfterMultilead("survey"));
        },
        onShowSimilar: () => {
            // This multilead never follows a sent lead - drop any post-lead state a previous run left behind
            setPostLeadMultilead(null);

            if (isMultileadTinderEnabled && flowState.propertyId && flowState.offerId && flowState.vendorId) {
                dispatch(closeFlow());
                dispatch(
                    openTinderMultiLeadModal({
                        originPropertyId: flowState.propertyId,
                        originOfferId: flowState.offerId,
                        originVendorId: flowState.vendorId,
                        refererApplicationUuid: null,
                        sourceSection: ApplicationSourceSection.POPUP_MODAL,
                        customSourceSection: "Multilead Tinder - post price reveal",
                        ctaText: InquiryButtonTypeName.SHOW_SIMILAR_OFFERS_NEARBY,
                        overlayVariant: postPriceRevealOverlayVariant
                    })
                );
                return;
            }

            dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
        },
        onCloseFlow: () => {
            if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && userProfile) {
                subscribeToNotifications(userProfile);
            }
            dispatch(closeFlow());
        }
    };

    const onPreferencesSuccess = (reason?: HomeMatchBasicPreferencesFlowCompletionReason) => {
        if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && flowState.preferences) {
            const {regions, rooms, price} = flowState.preferences;
            saveNewsletterSearchData({
                ...(regions?.length ? {search: {regions}} : {}),
                ...(rooms ? {rooms} : {}),
                ...(price ? {price: {upper: price}} : {})
            });
        }

        // The survey is the last step of the post-lead path - the UPDATED_PREFERENCES confirmation modal
        // belongs to the plain sign-up path, where no lead (and no multilead) was involved
        if (isPostLeadMultilead) {
            handlers.onCloseFlow();
            return;
        }

        if (reason === "homeMatchSkipped") {
            handlers.onNextStep();
            return;
        }

        handlers.onCloseFlow();
    };

    return (
        <>
            {flowState.currentStep === RegistrationFlowStep.REGISTRATION && <AuthModalRegistrationFlow />}
            {flowState.currentStep === RegistrationFlowStep.PHONE_COMPLETION && (
                <UserUpdatePhoneNumberModal
                    isOpen
                    onSuccess={() => {
                        clearPhoneVerificationTimerLS();
                        handlers.onNextStep();
                    }}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PHONE_VERIFICATION && <UserPhoneVerification isOpen onSuccess={handlers.onNextStep} />}
            {flowState.currentStep === RegistrationFlowStep.LEAD_AFTER_REGISTRATION && (
                <LeadAfterRegistration
                    isOpen
                    onConfirm={handlers.onLeadConfirm}
                    onClose={handlers.onCloseFlow}
                    onShowSimilar={handlers.onShowSimilar}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.MULTILEAD && !isTinderDeckParked && (
                <RegistrationMultilead
                    onComplete={handlers.onMultileadComplete}
                    onClose={handlers.onMultileadClose}
                    originalApplicationResponse={postLeadMultilead?.applicationResponse ?? null}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PREFERENCES && (
                <HomeMatchBasicPreferencesFlow
                    isOpen
                    source={homeMatchInitials.source}
                    initialStep={homeMatchInitials.initialStep}
                    defaultHomeMatchBasicConsent={homeMatchInitials.consent}
                    propertyId={flowState.propertyId}
                    offerId={flowState.offerId}
                    overlayVariant={postPriceRevealOverlayVariant}
                    trackingContext={{
                        eventName: homeMatchBasicModalEventName,
                        eventCategory: homeMatchBasicEventCategory,
                        sourceModal: homeMatchBasicModalEventName
                    }}
                    amplitudeTrackingContext={
                        homeMatchInitials.source === "afterLead" &&
                        flowState.leadSource &&
                        flowState.offerId &&
                        flowState.propertyId &&
                        postLeadMultilead?.refererApplicationUuid
                            ? {
                                  source: flowState.leadSource,
                                  itemId: flowState.offerId,
                                  propertyId: flowState.propertyId,
                                  transactionId: postLeadMultilead.refererApplicationUuid
                              }
                            : undefined
                    }
                    onComplete={onPreferencesSuccess}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.UPDATED_PREFERENCES && (
                <UserPreferencesUpdateDataModal isOpen onClose={handlers.onCloseFlow} overlayVariant={postPriceRevealOverlayVariant} />
            )}
        </>
    );
};
import {useLayoutEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";

import {clearPhoneVerificationTimerLS} from "@pg-mono/user";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {useMultileadTinderAbTest} from "../../ab_testing/hooks/use_multilead_tinder_ab_test";
import {openTinderMultiLeadModal} from "../../application/state/tinder_multi_lead_slice";
import {IApplicationData} from "../../application/types/IApplicationData";
import {ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {useAppSelector} from "../../store/hooks";
import {getUserSourceDisplay} from "../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../user/api/patch_home_match_configuration";
import {HomeMatchBasicPreferencesFlow} from "../../user/components/home_match_basic_preferences_flow/HomeMatchBasicPreferencesFlow";
import {UserPreferencesUpdateDataModal} from "../../user/components/preferences/UserPreferencesUpdateDataModal";
import {UserPhoneVerification} from "../../user/components/UserPhoneVerification";
import {UserUpdatePhoneNumberModal} from "../../user/components/UserUpdatePhoneNumberModal";
import {
    HomeMatchBasicPreferencesFlowCompletionReason,
    HomeMatchBasicPreferencesFlowInitialStep,
    HomeMatchBasicPreferencesFlowSource
} from "../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {closeFlow, nextStep, RegistrationFlowStep, setStep} from "../state/registration_flow_slice";
import {AuthModalVariant} from "./auth_modal/AuthModalHeaderVariants";
import {LeadAfterRegistration} from "./registration_flow/LeadAfterRegistration";
import {RegistrationMultilead} from "./registration_flow/RegistrationMultilead";
import {AuthModalRegistrationFlow} from "./AuthModalRegistrationFlow";

export const RegistrationFlow = () => {
    const dispatch = useDispatch();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const {hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {saveNewsletterSearchData, subscribeToNotifications} = useSubscribeToNewsletter();
    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const {isMultileadTinderEnabled} = useMultileadTinderAbTest();
    const postPriceRevealOverlayVariant = flowState.useLeadAfterRegistration ? "strong" : undefined;
    const homeMatchBasicEventCategory = flowState.gtmSourceSection || (flowState.userSource ? getUserSourceDisplay(flowState.userSource) : "");
    const [homeMatchInitials, setHomeMatchInitials] = useState<{
        source: HomeMatchBasicPreferencesFlowSource;
        initialStep: HomeMatchBasicPreferencesFlowInitialStep;
        consent: boolean;
    }>({
        source: "survey",
        initialStep: "survey",
        consent: false
    });
    const homeMatchBasicModalEventName = homeMatchInitials.source === "survey" ? "modal_post_sign_up" : "modal_post_lead";
    // Set when the price-reveal lead is sent; the multilead runs right after it and `wasOpened` marks
    // that this multilead belongs to the sent lead, so the preferences survey follows it.
    const [postLeadMultilead, setPostLeadMultilead] = useState<{
        refererApplicationUuid: string | null;
        wasOpened: boolean;
        applicationResponse: IApplicationData | null;
    } | null>(null);
    const isPostLeadMultilead = Boolean(postLeadMultilead?.wasOpened);
    const tinderMultiLead = useAppSelector((state) => state.tinderMultiLead);
    const lastTinderMultiLeadRef = useRef(tinderMultiLead);
    const wasFlowActiveRef = useRef(flowState.isActive);
    // In the Tinder AB test variant the deck replaces the classic multilead modal, so the flow parks on
    // MULTILEAD (rendering nothing) until the deck closes and the preferences survey can take over.
    const isTinderDeckParked = isMultileadTinderEnabled && isPostLeadMultilead && flowState.currentStep === RegistrationFlowStep.MULTILEAD;

    const continueToPreferences = (isHomeMatchOn: boolean, surveySource: HomeMatchBasicPreferencesFlowSource) => {
        setHomeMatchInitials({source: surveySource, initialStep: isHomeMatchOn ? "survey" : "summary", consent: isHomeMatchOn});
        dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
    };

    useLayoutEffect(() => {
        const previousTinderMultiLead = lastTinderMultiLeadRef.current;
        lastTinderMultiLeadRef.current = tinderMultiLead;

        if (!previousTinderMultiLead?.isOpen || tinderMultiLead?.isOpen || !isTinderDeckParked) {
            return;
        }

        continueToPreferences(homeMatchConfig?.is_active ?? false, "afterLead");
    }, [tinderMultiLead, isTinderDeckParked]);

    // This component never unmounts (it early-returns while inactive), so without this reset the
    // previous run's sent lead would leak into the next flow's multilead and mark its offer as sent
    useLayoutEffect(() => {
        const wasActive = wasFlowActiveRef.current;
        wasFlowActiveRef.current = flowState.isActive;

        if (!wasActive && flowState.isActive) {
            setPostLeadMultilead(null);
        }
    }, [flowState.isActive]);

    if (!flowState.isActive) {
        return null;
    }

    // The preferences survey keeps the `afterLead` source for a lead that was already sent, so the
    // HomeMatch source section and the modal GTM events stay comparable with the previous order.
    const getSurveySourceAfterMultilead = (multileadSource: HomeMatchBasicPreferencesFlowSource) => (isPostLeadMultilead ? "afterLead" : multileadSource);

    // Multilead (classic or Tinder, same AB test) shown right after the price-reveal lead was sent
    const openPostLeadMultilead = (sentApplication: IApplicationData | null) => {
        const {propertyId, offerId, vendorId} = flowState;
        const refererApplicationUuid = sentApplication?.uuid ?? null;

        if (!propertyId || !offerId || !vendorId) {
            // No offers to recommend - keep the uuid for the survey analytics and fall through to the survey
            setPostLeadMultilead({refererApplicationUuid, wasOpened: false, applicationResponse: sentApplication});
            return false;
        }

        setPostLeadMultilead({refererApplicationUuid, wasOpened: true, applicationResponse: sentApplication});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));

        if (isMultileadTinderEnabled) {
            dispatch(
                openTinderMultiLeadModal({
                    originPropertyId: propertyId,
                    originOfferId: offerId,
                    originVendorId: vendorId,
                    refererApplicationUuid,
                    sourceSection: ApplicationSourceSection.POPUP_MODAL,
                    customSourceSection: "Multilead Tinder - post price reveal lead",
                    overlayVariant: postPriceRevealOverlayVariant
                })
            );
        }

        return true;
    };

    const handlers = {
        onNextStep: () => {
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onLeadConfirm: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentApplication: IApplicationData | null = null) => {
            // Reversed order: propose the extra offers first, ask about preferences afterwards
            if (source === "afterLead" && openPostLeadMultilead(sentApplication)) {
                return;
            }

            continueToPreferences(isHomeMatchOn, source);
        },
        onMultileadComplete: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource) => {
            continueToPreferences(isHomeMatchOn, getSurveySourceAfterMultilead(source));
        },
        onMultileadClose: (isHomeMatchOn: boolean) => {
            continueToPreferences(isHomeMatchOn, getSurveySourceAfterMultilead("survey"));
        },
        onShowSimilar: () => {
            // This multilead never follows a sent lead - drop any post-lead state a previous run left behind
            setPostLeadMultilead(null);

            if (isMultileadTinderEnabled && flowState.propertyId && flowState.offerId && flowState.vendorId) {
                dispatch(closeFlow());
                dispatch(
                    openTinderMultiLeadModal({
                        originPropertyId: flowState.propertyId,
                        originOfferId: flowState.offerId,
                        originVendorId: flowState.vendorId,
                        refererApplicationUuid: null,
                        sourceSection: ApplicationSourceSection.POPUP_MODAL,
                        customSourceSection: "Multilead Tinder - post price reveal",
                        ctaText: InquiryButtonTypeName.SHOW_SIMILAR_OFFERS_NEARBY,
                        overlayVariant: postPriceRevealOverlayVariant
                    })
                );
                return;
            }

            dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
        },
        onCloseFlow: () => {
            if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && userProfile) {
                subscribeToNotifications(userProfile);
            }
            dispatch(closeFlow());
        }
    };

    const onPreferencesSuccess = (reason?: HomeMatchBasicPreferencesFlowCompletionReason) => {
        if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && flowState.preferences) {
            const {regions, rooms, price} = flowState.preferences;
            saveNewsletterSearchData({
                ...(regions?.length ? {search: {regions}} : {}),
                ...(rooms ? {rooms} : {}),
                ...(price ? {price: {upper: price}} : {})
            });
        }

        // The survey is the last step of the post-lead path - the UPDATED_PREFERENCES confirmation modal
        // belongs to the plain sign-up path, where no lead (and no multilead) was involved
        if (isPostLeadMultilead) {
            handlers.onCloseFlow();
            return;
        }

        if (reason === "homeMatchSkipped") {
            handlers.onNextStep();
            return;
        }

        handlers.onCloseFlow();
    };

    return (
        <>
            {flowState.currentStep === RegistrationFlowStep.REGISTRATION && <AuthModalRegistrationFlow />}
            {flowState.currentStep === RegistrationFlowStep.PHONE_COMPLETION && (
                <UserUpdatePhoneNumberModal
                    isOpen
                    onSuccess={() => {
                        clearPhoneVerificationTimerLS();
                        handlers.onNextStep();
                    }}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PHONE_VERIFICATION && <UserPhoneVerification isOpen onSuccess={handlers.onNextStep} />}
            {flowState.currentStep === RegistrationFlowStep.LEAD_AFTER_REGISTRATION && (
                <LeadAfterRegistration
                    isOpen
                    onConfirm={handlers.onLeadConfirm}
                    onClose={handlers.onCloseFlow}
                    onShowSimilar={handlers.onShowSimilar}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.MULTILEAD && !isTinderDeckParked && (
                <RegistrationMultilead
                    onComplete={handlers.onMultileadComplete}
                    onClose={handlers.onMultileadClose}
                    originalApplicationResponse={postLeadMultilead?.applicationResponse ?? null}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PREFERENCES && (
                <HomeMatchBasicPreferencesFlow
                    isOpen
                    source={homeMatchInitials.source}
                    initialStep={homeMatchInitials.initialStep}
                    defaultHomeMatchBasicConsent={homeMatchInitials.consent}
                    propertyId={flowState.propertyId}
                    offerId={flowState.offerId}
                    overlayVariant={postPriceRevealOverlayVariant}
                    trackingContext={{
                        eventName: homeMatchBasicModalEventName,
                        eventCategory: homeMatchBasicEventCategory,
                        sourceModal: homeMatchBasicModalEventName
                    }}
                    amplitudeTrackingContext={
                        homeMatchInitials.source === "afterLead" &&
                        flowState.leadSource &&
                        flowState.offerId &&
                        flowState.propertyId &&
                        postLeadMultilead?.refererApplicationUuid
                            ? {
                                  source: flowState.leadSource,
                                  itemId: flowState.offerId,
                                  propertyId: flowState.propertyId,
                                  transactionId: postLeadMultilead.refererApplicationUuid
                              }
                            : undefined
                    }
                    onComplete={onPreferencesSuccess}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.UPDATED_PREFERENCES && (
                <UserPreferencesUpdateDataModal isOpen onClose={handlers.onCloseFlow} overlayVariant={postPriceRevealOverlayVariant} />
            )}
        </>
    );
};
